import ApiError from "../network/ApiError";
import Network from "../network/network";

export default class Logger {
    private static readonly LOGGER_SOURCE = "OutlookPlugin";

    /**
     * How long the same error stays off the relay. A refused endpoint answers every caller with the
     * same refusal, and one relayed entry per caller says no more than the first one does.
     */
    private static readonly RELAY_SUPPRESSION_MS = 5 * 60 * 1000;
    /** Guards the bookkeeping below against growing without bound over a long taskpane session. */
    private static readonly MAX_RELAYED_ENTRIES = 200;

    private static relayedAt: { [message: string]: number } = {};

    private network: Network;

    constructor(network: Network) {
        this.network = network;
    }

    public log(level: Loglevel, msg: string, trace?: any) {
        if (level === Loglevel.Info || level === Loglevel.Warning) {
            console.log(`%c ${level} - ${msg}\n`, `color: ${this.getColor(level)}`, trace);
        } else if (level === Loglevel.Error) {
            console.error(`%c ${level} - ${msg}\n`, `color: ${this.getColor(level)}`, trace);
            if (Logger.isInTheBackendLogAlready(trace)) {
                return;
            }
            const message = `${msg} ${trace}`;
            if (Logger.shouldRelay(message)) {
                // Fire and forget - the relay must never take the caller down, and a failing relay
                // cannot be reported through itself.
                this.network
                    .sendApi("writelog", this.getOdataLogObject(Loglevel.Error, message))
                    .catch(e => console.error("could not relay log entry", e));
            }
        }
    }

    /**
     * A request the backend refused with 401 or 403. The backend logs every request it refuses, with route,
     * user and company, so relaying it as well only doubled each refusal with a writelog request - one per
     * opened mail for a company that lacks a right, which no suppression window can stop once the taskpane
     * is loaded anew for every mail.
     */
    private static isInTheBackendLogAlready = (trace: any): boolean => {
        return ApiError.isApiError(trace) && trace.isAuthorizationError;
    };

    /**
     * Relays an error at most once per suppression window. Without this the plugin answers a refused
     * endpoint with one writelog request per attempt - the very hammering the errors report.
     */
    private static shouldRelay = (message: string): boolean => {
        const now = Date.now();
        const lastRelayedAt = Logger.relayedAt[message];
        if (lastRelayedAt && now - lastRelayedAt < Logger.RELAY_SUPPRESSION_MS) {
            return false;
        }
        if (Object.keys(Logger.relayedAt).length >= Logger.MAX_RELAYED_ENTRIES) {
            // Entries past their window suppress nothing; only if none are left does the rest go, which
            // costs one extra relay per message and is still better than growing without bound.
            Object.keys(Logger.relayedAt)
                .filter(key => now - Logger.relayedAt[key] >= Logger.RELAY_SUPPRESSION_MS)
                .forEach(key => delete Logger.relayedAt[key]);
            if (Object.keys(Logger.relayedAt).length >= Logger.MAX_RELAYED_ENTRIES) {
                Logger.relayedAt = {};
            }
        }
        Logger.relayedAt[message] = now;
        return true;
    };

    private getColor = (level: Loglevel) => {
        switch (level) {
            case Loglevel.Error:
                return "#FF2121";
            case Loglevel.Warning:
                return "#FFFF21";
            case Loglevel.Info:
                return "";
        }
    };

    private getOdataLogObject(logLevel: Loglevel, message: string): any {
        return {
            Source: Logger.LOGGER_SOURCE,
            Level: Logger.getLevelAsString(logLevel),
            Message: message,
            Timestamp: new Date().toISOString(),
        };
    }

    private static getLevelAsString(logLevel: Loglevel): string {
        switch (logLevel) {
            case Loglevel.Error:
                return "Error";
            case Loglevel.Warning:
                return "Warn";
            case Loglevel.Info:
                return "Info";
        }
    }
}

export enum Loglevel {
    Info = "Info",
    Warning = "Warning",
    Error = "Error",
}
